import { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { COUPLE } from "@/lib/constants";
import { useReveal } from "@/contexts/RevealContext";
import ParticleBurst from "@/components/ParticleBurst";
import { playShimmerChime } from "@/lib/chime";

const BURST_THRESHOLD = 45; // %
const REVEAL_THRESHOLD = 55; // %

export default function ScratchCard() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const [localRevealed, setLocalRevealed] = useState(false);
  const [burst, setBurst] = useState(false);
  const [glow, setGlow] = useState(false);

  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const burstFiredRef = useRef(false);
  const lastProgressAtRef = useRef(0);

  const { revealed: globalRevealed, reveal } = useReveal();

  // If already revealed globally (sessionStorage), skip canvas
  useEffect(() => {
    if (globalRevealed) setLocalRevealed(true);
  }, [globalRevealed]);

  useEffect(() => {
    if (localRevealed) return;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const paint = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      const grad = ctx.createLinearGradient(0, 0, rect.width, rect.height);
      grad.addColorStop(0, "#3D0A17");
      grad.addColorStop(0.5, "#5C1123");
      grad.addColorStop(1, "#8C1A35");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, rect.width, rect.height);

      for (let i = 0; i < 110; i++) {
        ctx.fillStyle = `rgba(212,175,55,${0.08 + Math.random() * 0.2})`;
        ctx.beginPath();
        ctx.arc(
          Math.random() * rect.width,
          Math.random() * rect.height,
          Math.random() * 1.8,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }

      ctx.fillStyle = "#F3E5AB";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "italic 600 30px 'Cormorant Garamond', serif";
      ctx.fillText("Scratch to Reveal ✨", rect.width / 2, rect.height / 2 - 14);
      ctx.font = "400 11px 'Outfit', sans-serif";
      ctx.fillStyle = "rgba(253, 251, 247, 0.75)";
      ctx.fillText("DRAG TO UNCOVER THE DATE", rect.width / 2, rect.height / 2 + 16);
      ctx.font = "italic 500 13px 'Cormorant Garamond', serif";
      ctx.fillStyle = "rgba(212, 175, 55, 0.85)";
      ctx.fillText("#PandA", rect.width / 2, rect.height / 2 + 40);
    };

    paint();
    window.addEventListener("resize", paint);
    return () => window.removeEventListener("resize", paint);
  }, [localRevealed]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const scratchAt = (x, y) => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.lineWidth = 42;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastRef.current = { x, y };
  };

  const computeProgress = () => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    const step = Math.max(1, Math.floor((w * h) / 6000));
    const data = ctx.getImageData(0, 0, w, h).data;
    let cleared = 0;
    let total = 0;
    for (let i = 3; i < data.length; i += 4 * step) {
      total += 1;
      if (data[i] < 32) cleared += 1;
    }
    return (cleared / total) * 100;
  };

  const triggerBurst = () => {
    if (burstFiredRef.current) return;
    burstFiredRef.current = true;
    setBurst(true);
    setGlow(true);
    playShimmerChime();
    // Clean up burst DOM after animation
    setTimeout(() => setBurst(false), 2400);
    setTimeout(() => setGlow(false), 2000);
  };

  const evaluate = ({ live = false } = {}) => {
    const pct = computeProgress();
    if (pct >= BURST_THRESHOLD && !burstFiredRef.current) {
      triggerBurst();
    }
    // Only finalize reveal on release (end) or button — not during drag —
    // so the user sees the burst unfold before the veil drops.
    if (!live && pct >= REVEAL_THRESHOLD && !localRevealed) {
      setLocalRevealed(true);
      reveal();
    }
  };

  const start = (e) => {
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    scratchAt(x, y);
    // Throttle live progress checks
    const now = performance.now();
    if (now - lastProgressAtRef.current > 160) {
      lastProgressAtRef.current = now;
      evaluate({ live: true });
    }
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    evaluate({ live: false });
  };

  const revealAll = () => {
    triggerBurst();
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    // Slight delay so the burst is visible before the veil fully lifts
    setTimeout(() => {
      setLocalRevealed(true);
      reveal();
    }, 650);
  };

  return (
    <div
      data-testid="scratch-card"
      ref={wrapRef}
      className={`relative w-full max-w-lg mx-auto h-72 md:h-80 rounded-3xl overflow-hidden border border-gold/35 shadow-[0_18px_60px_rgba(0,0,0,0.55)] transition-shadow duration-700 ${
        glow ? "scratch-card-burst" : ""
      }`}
    >
      {/* Hidden reveal layer — content masked until revealed */}
      <div className="absolute inset-0 bg-gradient-to-br from-ink-800 via-burgundy-deep to-ink-900 flex flex-col items-center justify-center px-6 text-center">
        <div className="text-[0.6rem] uppercase tracking-[0.4em] text-gold mb-3">
          Save the date
        </div>
        <div
          data-testid="scratch-date"
          className="font-serif text-4xl md:text-5xl gold-text leading-none"
        >
          {localRevealed ? COUPLE.weddingDateLabel : "✧ ✧ ✧ ✧ ✧"}
        </div>
        <div className="mt-3 text-ivory-muted text-sm tracking-wide">
          {localRevealed ? (
            <>{COUPLE.muhurta} · {COUPLE.venue}</>
          ) : (
            <span className="italic text-ivory-dim">A surprise awaits…</span>
          )}
        </div>
        <div className="mt-5 font-serif italic text-2xl hashtag-glow">
          #PandA ❤️
        </div>
      </div>

      {!localRevealed && (
        <canvas
          ref={canvasRef}
          data-testid="scratch-canvas"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className="absolute inset-0 cursor-crosshair touch-none"
        />
      )}

      {/* Gold-dust sparkle burst — one-shot per mount */}
      <ParticleBurst active={burst} />

      {!localRevealed && (
        <button
          type="button"
          onClick={revealAll}
          data-testid="scratch-reveal-button"
          className="absolute bottom-3 right-3 z-40 text-[0.6rem] uppercase tracking-[0.32em] px-3 py-1.5 rounded-full border border-gold/50 text-gold bg-ink-900/60 hover:bg-burgundy/40"
        >
          <Sparkles size={11} className="inline mr-1 -mt-0.5" /> Reveal
        </button>
      )}
    </div>
  );
}
